import FingerprintJS from "@fingerprintjs/fingerprintjs";

import { CachedDataCollector } from "src/core/base/CachedDataCollector";
import { SDK_VERSION } from "src/core/constants";
import { TrackerVariableType } from "src/core/model/TrackerVariableType";
import { CacheStrategy } from "src/core/model/VariableCollectionMetadata";
import { BrowserDetector } from "src/util/BrowserDetector";

/**
 * Collects device/browser/environment information.
 * Mirrors Android DeviceCollector — adapted for web environment.
 */
export class DeviceCollector extends CachedDataCollector {
  private fingerprintPromise: Promise<string> | null = null;
  private resolvedFingerprint: string | null = null;
  private initTimestamp = Date.now();

  collect(): Map<TrackerVariableType, unknown> {
    const data = new Map<TrackerVariableType, unknown>();

    this.collectWithCache(data, TrackerVariableType.DEVICE_FINGERPRINT, CacheStrategy.HEAVY, () =>
      this.getFingerprint(),
    );
    this.collectWithCache(
      data,
      TrackerVariableType.SCREEN_RESOLUTION,
      CacheStrategy.HEAVY,
      () => `${screen.width}x${screen.height}`,
    );
    this.collectWithCache(data, TrackerVariableType.USER_AGENT, CacheStrategy.HEAVY, () => navigator.userAgent);
    this.collectWithCache(data, TrackerVariableType.BROWSER, CacheStrategy.HEAVY, () => {
      const b = BrowserDetector.getBrowser();
      return `${b.name} ${b.version}`;
    });
    this.collectWithCache(data, TrackerVariableType.OS, CacheStrategy.HEAVY, () => {
      const os = BrowserDetector.getOS();
      return `${os.name} ${os.version}`;
    });
    this.collectWithCache(data, TrackerVariableType.LANGUAGE, CacheStrategy.MEDIUM, () =>
      BrowserDetector.getLanguage(),
    );
    this.collectWithCache(data, TrackerVariableType.TIMEZONE, CacheStrategy.MEDIUM, () =>
      BrowserDetector.getTimezone(),
    );
    this.collectWithCache(data, TrackerVariableType.PLATFORM, CacheStrategy.HEAVY, () => BrowserDetector.getPlatform());
    this.collectWithCache(data, TrackerVariableType.SUSPICIOUS_BROWSER, CacheStrategy.SLOW, () =>
      BrowserDetector.isSuspiciousBrowser(),
    );
    this.collectWithCache(data, TrackerVariableType.HEADLESS_INDICATOR_COUNT, CacheStrategy.SLOW, () =>
      BrowserDetector.getHeadlessIndicatorCount(),
    );
    this.collectWithCache(data, TrackerVariableType.THEME_MODE, CacheStrategy.MEDIUM, () =>
      window.matchMedia?.("(prefers-color-scheme: dark)").matches ? "DARK" : "LIGHT",
    );

    // Non-cached (always fresh)
    this.collectIfNeeded(data, TrackerVariableType.SDK_VERSION, () => SDK_VERSION);
    this.collectIfNeeded(data, TrackerVariableType.TIMESTAMP, () => Date.now());
    this.collectIfNeeded(data, TrackerVariableType.ELAPSED_TIME, () => Date.now() - this.initTimestamp);

    return data;
  }

  /**
   * Returns the resolved fingerprint synchronously if available,
   * otherwise returns the pending Promise.
   */
  private getFingerprint(): string | Promise<string> {
    if (this.resolvedFingerprint) return this.resolvedFingerprint;
    if (!this.fingerprintPromise) {
      this.fingerprintPromise = FingerprintJS.load()
        .then((fp) => fp.get())
        .then((result) => {
          this.resolvedFingerprint = result.visitorId;
          return result.visitorId;
        })
        .catch(() => {
          this.resolvedFingerprint = "unavailable";
          return "unavailable";
        });
    }
    return this.fingerprintPromise;
  }

  /**
   * Pre-load fingerprint so it's ready when collect() is called.
   * Must be awaited before the first event fires.
   */
  async initFingerprint(): Promise<string> {
    const result = this.getFingerprint();
    if (result instanceof Promise) {
      const resolved = await result;
      this.resolvedFingerprint = resolved;
      this.invalidateCache(TrackerVariableType.DEVICE_FINGERPRINT);
      return resolved;
    }
    return result;
  }
}
